export const name = "JSON_QUERY";
export const summary = `Extracts a JSON value and converts it to a SQL JSON-formatted \`STRING\` or \`JSON\` value.`;
export const reference = `
## JSON_QUERY

Extracts a JSON value and converts it to a SQL JSON-formatted \`STRING\` or \`JSON\` value.

\`\`\`
JSON_QUERY(json_string_expr, json_path)
\`\`\`

### 

\`\`\`
JSON_QUERY(json_expr, json_path)
\`\`\`


### Description

Extracts a JSON value and converts it to a SQL JSON-formatted \`STRING\` or\`JSON\` value. This function uses double quotes to escape invalid JSONPath characters in JSON keys. For example: \`"a.b"\`.

Arguments:

- \`json_string_expr\`: A JSON-formatted string. For example:

- \`json_expr\`: JSON. For example:

- \`json_path\`: The JSONPath. This identifies the data that you want to obtain from the input. This function lets you specify a mode for the JSONPath.

There are differences between the JSON-formatted string and JSON input types. For details, see Differences between the JSON and JSON-formatted STRING types.


### Return type

- \`json_string_expr\`: A JSON-formatted \`STRING\`
- \`json_expr\`: \`JSON\`


### Examples

In the following example, JSON data is extracted and returned as JSON.



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": [{"id": 5}, {"id": 12}]}}',
    '$.class') AS json_data;

/*-----------------------------------*
 | json_data |
 +-----------------------------------+
 | {"students":[{"id":5},{"id":12}]} |
 *-----------------------------------*/
\`\`\`

In the following examples, JSON data is extracted and returned as JSON-formatted strings.



\`\`\`
SELECT
  JSON_QUERY('{"class": {"students": [{"name": "Jane"}]}}', '$') AS json_text_string;

/*-----------------------------------------------------------*
 | json_text_string |
 +-----------------------------------------------------------+
 | {"class":{"students":[{"name":"Jane"}]}} |
 *-----------------------------------------------------------*/
\`\`\`



\`\`\`
SELECT JSON_QUERY('{"class": {"students": []}}', '$') AS json_text_string;

/*-----------------------------------------------------------*
 | json_text_string |
 +-----------------------------------------------------------+
 | {"class":{"students":[]}} |
 *-----------------------------------------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "John"},{"name": "Jamie"}]}}',
    '$') AS json_text_string;

/*-----------------------------------------------------------*
 | json_text_string |
 +-----------------------------------------------------------+
 | {"class":{"students":[{"name":"John"},{"name":"Jamie"}]}} |
 *-----------------------------------------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "Jane"}]}}',
    '$.class.students[0]') AS first_student;

/*-----------------*
 | first_student |
 +-----------------+
 | {"name":"Jane"} |
 *-----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY('{"class": {"students": []}}', '$.class.students[0]') AS first_student;

/*-----------------*
 | first_student |
 +-----------------+
 | NULL |
 *-----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "John"}, {"name": "Jamie"}]}}',
    '$.class.students[0]') AS first_student;

/*-----------------*
 | first_student |
 +-----------------+
 | {"name":"John"} |
 *-----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "Jane"}]}}',
    '$.class.students[1].name') AS second_student;

/*----------------*
 | second_student |
 +----------------+
 | NULL |
 *----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": []}}',
    '$.class.students[1].name') AS second_student;

/*----------------*
 | second_student |
 +----------------+
 | NULL |
 *----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "John"}, {"name": null}]}}',
    '$.class.students[1].name') AS second_student;

/*----------------*
 | second_student |
 +----------------+
 | NULL |
 *----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "John"}, {"name": "Jamie"}]}}',
    '$.class.students[1].name') AS second_student;

/*----------------*
 | second_student |
 +----------------+
 | "Jamie" |
 *----------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "Jane"}]}}',
    '$.class."students"') AS student_names;

/*------------------------------------*
 | student_names |
 +------------------------------------+
 | [{"name":"Jane"}] |
 *------------------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": []}}',
    '$.class."students"') AS student_names;

/*------------------------------------*
 | student_names |
 +------------------------------------+
 | [] |
 *------------------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    '{"class": {"students": [{"name": "John"}, {"name": "Jamie"}]}}',
    '$.class."students"') AS student_names;

/*------------------------------------*
 | student_names |
 +------------------------------------+
 | [{"name":"John"},{"name":"Jamie"}] |
 *------------------------------------*/
\`\`\`

In the following examples, the JSON data is extracted in lax mode. Because the keyword \`lax\` is included in the \`JSONPath\`, JSON arrays are automatically unwrapped.



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": [{"name": "Jane"}]}}',
    'lax $.class.students.name') AS student_names_lax;

/*-------------------*
 | student_names_lax |
 *-------------------*
 | ["Jane"] |
 *-------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    JSON '[{"class": {"students": [{"name": "Joe"}, {"name": "Jamie"}]}}]',
    'lax $.class.students.name') AS student_names_lax;

/*-------------------*
 | student_names_lax |
 *-------------------*
 | ["Joe","Jamie"] |
 *-------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": [[{"name": "John"}], {"name": "Jamie"}]}}',
    'lax $.class.students.name') AS student_names_lax;

/*-------------------*
 | student_names_lax |
 *-------------------*
 | ["Jamie"] |
 *-------------------*/
\`\`\`

In the following examples, the JSON data is extracted in lax recursive mode. Because the keyword \`lax recursive\` is included in the \`JSONPath\`, JSON arrays are unwrapped until a non-array type is found.



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": [{"name": "Jane"}]}}',
    'lax recursive $.class.students.name') AS student_names_lax_recursive;

/*-----------------------------*
 | student_names_lax_recursive |
 *-----------------------------*
 | ["Jane"] |
 *-----------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    JSON '[[{"class": {"students": [{"name": "Joe"}, {"name": "Jamie"}]}}]]',
    'lax recursive $.class.students.name') AS student_names_lax_recursive;

/*-----------------------------*
 | student_names_lax_recursive |
 *-----------------------------*
 | ["Joe","Jamie"] |
 *-----------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": [[{"name": "John"}], {"name": "Jamie"}]}}',
    'lax recursive $.class.students.name') AS student_names_lax_recursive;

/*-----------------------------*
 | student_names_lax_recursive |
 *-----------------------------*
 | ["John","Jamie"] |
 *-----------------------------*/
\`\`\`

In the following examples, the keywords \`lax\` and \`lax recursive\` indicate that non-array types should be wrapped into arrays of size 1 before matching. The modes \`lax\` and \`lax recursive\` behave identically for wrapping arrays.



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": {"name": "Jane"}}}',
    'lax $.class[0].students[0].name') AS student_names_lax,
  JSON_QUERY(
    JSON '{"class": {"students": {"name": "Jane"}}}',
    'lax recursive $.class[0].students[0].name') AS student_names_lax_recursive;

/*-------------------*-----------------------------*
 | student_names_lax | student_names_lax_recursive |
 *-------------------*-----------------------------*
 | ["Jane"] | ["Jane"] |
 *-------------------*-----------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    JSON '[{"class": {"students": [{"name": "Joe"}, {"name": "Jamie"}]}}]',
    'lax $.class[0].students[0].name') AS student_names_lax,
  JSON_QUERY(
    JSON '[{"class": {"students": [{"name": "Joe"}, {"name": "Jamie"}]}}]',
    'lax recursive $.class[0].students[0].name') AS student_names_lax_recursive;

/*-------------------*-----------------------------*
 | student_names_lax | student_names_lax_recursive |
 *-------------------*-----------------------------*
 | ["Joe"] | ["Joe"] |
 *-------------------*-----------------------------*/
\`\`\`



\`\`\`
SELECT
  JSON_QUERY(
    JSON '{"class": {"students": [[{"name": "John"}], {"name": "Jamie"}]}}',
    'lax $.class[0].students[0].name') AS student_names_lax,
  JSON_QUERY(
    JSON '{"class": {"students": [[{"name": "John"}], {"name": "Jamie"}]}}',
    'lax recursive $.class[0].students[0].name') AS student_names_lax_recursive;

/*-------------------*-----------------------------*
 | student_names_lax | student_names_lax_recursive |
 *-------------------*-----------------------------*
 | ["John"] | ["John"] |
 *-------------------*-----------------------------*/
\`\`\`



\`\`\`
SELECT JSON_QUERY('{"a": null}', "$.a"); -- Returns a SQL NULL
SELECT JSON_QUERY('{"a": null}', "$.b"); -- Returns a SQL NULL
\`\`\`



\`\`\`
SELECT JSON_QUERY(JSON '{"a": null}', "$.a"); -- Returns a JSON 'null'
SELECT JSON_QUERY(JSON '{"a": null}', "$.b"); -- Returns a SQL NULL
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/json_functions
`;
